import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { churchApi } from "api/api";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Images
import bgImage from "assets/images/bg-sign-up-cover.jpeg";

function Cover() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    abbreviation: "",
    gps: "",
    telephone: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [registeredChurch, setRegisteredChurch] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ["name", "email", "address", "abbreviation", "telephone"];

    requiredFields.forEach((field) => {
      if (!formData[field]) newErrors[field] = "This field is required";
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setOpenError(true);
      setErrorMessage("Please fill all required fields correctly");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await churchApi.register(formData);
      setRegisteredChurch(response);
      setOpenSuccess(true);
    } catch (error) {
      console.error("Registration error:", error);
      if (error.errors) setErrors(error.errors);
      setOpenError(true);
      setErrorMessage(error.message || "Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSuccess(false);
    setOpenError(false);
  };

  const handleContinueToUserRegistration = () => {
    if (registeredChurch) {
      navigate(
        `/authentication/register/${registeredChurch.id}/${encodeURIComponent(
          registeredChurch.name
        )}`
      );
    }
  };

  return (
    <CoverLayout image={bgImage}>
      <Card sx={{ width: "100%", maxWidth: "900px", mx: "auto" }}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Register your church
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Fill in the form below
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="name"
                label="Church Name *"
                variant="standard"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
              />
              <MDInput
                type="email"
                name="email"
                label="Email *"
                variant="standard"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </MDBox>

            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="address"
                label="Address *"
                variant="standard"
                fullWidth
                value={formData.address}
                onChange={handleChange}
                error={!!errors.address}
                helperText={errors.address}
              />
              <MDInput
                type="text"
                name="abbreviation"
                label="Abbreviation *"
                variant="standard"
                fullWidth
                value={formData.abbreviation}
                onChange={handleChange}
                error={!!errors.abbreviation}
                helperText={errors.abbreviation}
              />
            </MDBox>

            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="gps"
                label="GPS Address"
                variant="standard"
                fullWidth
                value={formData.gps}
                onChange={handleChange}
                error={!!errors.gps}
                helperText={errors.gps}
              />
              <MDInput
                type="text"
                name="telephone"
                label="Telephone *"
                variant="standard"
                fullWidth
                value={formData.telephone}
                onChange={handleChange}
                error={!!errors.telephone}
                helperText={errors.telephone}
              />
            </MDBox>

            <MDBox display="flex" alignItems="center" ml={-1} mb={2}>
              <Checkbox required />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;I agree to the&nbsp;
              </MDTypography>
              <MDTypography
                component="a"
                href="#"
                variant="button"
                fontWeight="bold"
                color="info"
                textGradient
              >
                Terms and Conditions
              </MDTypography>
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? "Registering..." : "Register Church"}
              </MDButton>
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign In
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Success Notification */}
      <Snackbar
        open={openSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
          action={
            <MDButton color="white" size="small" onClick={handleContinueToUserRegistration}>
              Continue
            </MDButton>
          }
        >
          Church registered successfully! Continue to create admin account.
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={openError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </CoverLayout>
  );
}

export default Cover;
